import React, { useCallback, useContext, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { TeamHomeStackNavigationProp } from "../TeamHomeStack";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { TeamNavigatorContext } from "../../TeamNavigatorContext";
import { TeamQuery } from "./__generated__/TeamQuery.graphql";
import { TeamDefaultTodoCategory } from "./TeamDefaultTodoCategory";
import { useSizeClass } from "../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useHasPermissions } from "../../../../OS/Login/usePermissions";
import { Box, useBoxStyle, UserOrEmployeeAvatar } from "arkh";
import { TeamTeamSchedule } from "./TeamTeamSchedule";
import { TeamTeamSchedulePreview } from "./TeamTeamSchedulePreview";
import { TeamFeatureBar } from "./TeamFeatureBar/TeamFeatureBar";

import { TeamUrgentSignals } from "./TeamUrgentSignals";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

import { TeamUpdateInfoButton } from "./TeamAppInfoButton";

function InnerTeam(props: {
  navigation: TeamHomeStackNavigationProp<"TeamHomeMain">;
  fetchKey: string;
}) {
  const teamNavigatorContext = useContext(TeamNavigatorContext);
  const teamId = teamNavigatorContext.teamId;

  const { currentEmployeeId } = usePersistedEmployeeId();
  const data = useLazyLoadQuery<TeamQuery>(
    graphql`
      query TeamQuery(
        $teamId: ID!
        $first: Int
        $after: String
        $status: TodoStatus
        $employeeId: ID
        $isEmployee: Boolean!
      ) @throwOnFieldError {
        viewer {
          appInfo {
            ...TeamAppInfoButton_appInfo
            __typename
          }
        }
        store {
          myRole
          ...usePermissions_store
          employee(id: $employeeId) @include(if: $isEmployee) {
            ...Avatar_employee
          }
          me @skip(if: $isEmployee) {
            user {
              ...Avatar_user
            }
            employee {
              ...Avatar_employee
            }
          }
          team(id: $teamId) {
            id
            name
            ...TeamTeamSchedule_team
            ...TeamTeamSchedulePreview_team
            ...TeamFeatureBar_team
            ...TeamUrgentSignals_team
            urgentSignals {
              __typename
            }
            defaultTodoCategory {
              ...TeamDefaultTodoCategory_TodoCategoryTeam
            }
          }
        }
      }
    `,
    {
      teamId,
      first: 30,
      status: "PENDING",
      employeeId: currentEmployeeId,
      isEmployee: !!currentEmployeeId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const openTodo = useCallback(
    (autofocusResponse: boolean, todoId: string) => {
      props.navigation.navigateDeprecated("TodoEdit", {
        todoId,
        autofocusResponse,
      });
    },
    [props.navigation]
  );

  const openTodoCategory = useCallback(
    (categoryId: string, categoryName: string) => {
      if (!data.store.team) {
        return;
      }
      props.navigation.navigateDeprecated("TodoCategory", {
        id: categoryId,
        teamId: data.store.team.id,
        teamName: data.store.team.name,
        title: categoryName,
        fetchKey: new Date().toISOString(),
      });
    },
    [data.store.team, props.navigation]
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const hasRoutineAdmin = useHasPermissions(data.store, "ROUTINE_ADMIN");
  const contentContainerStyle = useBoxStyle({
    spaceBottom: "xxlarge",
  });
  const onOpenFeature = useCallback(
    (
      feature:
        | "KPI"
        | "FILE"
        | "SIGNAL"
        | "EMPLOYEE_SCHEDULE"
        | "PRODUCT"
        | "PRODUCT_DATE_TRACKER"
        | "CALENDAR"
    ): void => {
      switch (feature) {
        case "CALENDAR":
          props.navigation.navigate("CalendarTeam", {
            teamId: data.store!.team!.id,
          });
          break;
        case "KPI":
          props.navigation.navigateDeprecated("KPITeam", {
            teamId: data.store!.team!.id,
          });
          break;
        case "FILE":
          props.navigation.navigateDeprecated("Files", {});
          break;
        case "SIGNAL":
          props.navigation.navigateDeprecated("Signals", {
            teamId: data.store!.team!.id,
          });
          break;
        case "EMPLOYEE_SCHEDULE":
          props.navigation.navigateDeprecated("EmployeeSchedule", {
            teamIds: [data.store.team!.id],
          });
          break;
        case "PRODUCT":
          props.navigation.navigateDeprecated("Products", {});
          break;
        case "PRODUCT_DATE_TRACKER":
          props.navigation.navigateDeprecated("ProductDateTracker", {});
          break;
      }
    },
    [data.store, props.navigation]
  );
  const openAdmin = useCallback(() => {
    if (data.store.team === null) {
      return;
    }
    props.navigation.navigateDeprecated("TeamScheduleAdmin", {
      teamId: data.store.team.id,
    });
  }, [data.store.team, props.navigation]);

  const onShowMore = useCallback(
    (focusFrequency: "DAILY" | "WEEKLY" | "MONTHLY") => {
      if (data.store.team === null) {
        return;
      }
      props.navigation.navigateDeprecated("TeamSchedule", {
        teamId: data.store.team.id,
        focusFrequency,
      });
    },
    [data.store.team, props.navigation]
  );

  const onOpenSignal = useCallback(
    (signalId: string) => {
      props.navigation.navigateDeprecated("SignalDetail", { signalId });
    },
    [props.navigation]
  );

  const onOpenSignals = useCallback(() => {
    props.navigation.navigateDeprecated("Signals", {
      teamId: data.store.team!.id,
    });
  }, [data.store.team, props.navigation]);

  const profileIndicator = useMemo(() => {
    return (
      <UserOrEmployeeAvatar
        user={data.store.me?.user ?? null}
        employee={data.store.me?.employee ?? data.store.employee ?? null}
        size="small"
        style="assigned-to-me"
      />
    );
  }, [data.store.me, data.store.employee]);

  const onPressUpdateInfo = useCallback(
    (linkURL: string) => {
      props.navigation.navigateDeprecated("WebView", {
        url: linkURL,
      });
    },
    [props.navigation]
  );
  const ListHeaderComponent = useMemo(() => {
    if (!data.store.team) {
      return null;
    }
    return (
      <>
        {data.viewer?.appInfo ? (
          <TeamUpdateInfoButton
            onPress={onPressUpdateInfo}
            appInfo={data.viewer.appInfo}
          />
        ) : null}

        <TeamFeatureBar
          team={data.store.team}
          onOpenTodoCategory={openTodoCategory}
          onOpenFeature={onOpenFeature}
          hideSignals={(data.store.team.urgentSignals?.length ?? 0) !== 0}
          profileIndicator={profileIndicator}
        />
        <TeamUrgentSignals
          team={data.store.team}
          onOpenSignal={onOpenSignal}
          onOpenSignals={onOpenSignals}
        />
        {isCompact ? (
          <TeamTeamSchedulePreview
            hasAdminAccess={hasRoutineAdmin}
            onOpenAdmin={openAdmin}
            onPress={onShowMore}
            team={data.store.team}
          />
        ) : null}
      </>
    );
  }, [
    data.store.team,
    hasRoutineAdmin,
    isCompact,
    onOpenFeature,
    onOpenSignal,
    onOpenSignals,
    onShowMore,
    openAdmin,
    openTodoCategory,
    profileIndicator,
    data.viewer?.appInfo,
    onPressUpdateInfo,
  ]);

  const addTodo = useCallback(
    (categoryId: string) => {
      props.navigation.navigate("TodoCreate", {
        categoryId,
        teamIds: [data.store.team!.id],
        title: "",
      });
    },
    [data.store.team, props.navigation]
  );

  if (!data.store.team) {
    return null;
  }

  return (
    <Box style={{ flexDirection: "row", flex: 1 }}>
      {!isCompact && data.store.team ? (
        <TeamTeamSchedule
          navigation={props.navigation}
          store={data.store}
          team={data.store.team}
        />
      ) : null}
      <Box style={{ width: isCompact ? "100%" : "55%" }}>
        {data.store.team.defaultTodoCategory ? (
          <TeamDefaultTodoCategory
            teamId={data.store.team.id}
            bottomSafeArea={!teamNavigatorContext.hasTabBar}
            contentContainerStyle={contentContainerStyle}
            ListHeaderComponent={ListHeaderComponent}
            defaultTodoCategory={data.store.team.defaultTodoCategory}
            openTodo={openTodo}
            openTodoCategory={openTodoCategory}
            addTodo={data.store.myRole === "READ_ONLY" ? undefined : addTodo}
          />
        ) : (
          ListHeaderComponent
        )}
      </Box>
    </Box>
  );
}

export function Team(props: {
  navigation: TeamHomeStackNavigationProp<"TeamHomeMain">;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerTeam {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}
